import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { tokenStorage } from '../services/tokenStorage';
import { adminApi } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuthStatus: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuthStatus = (): boolean => {
    const hasTokens = tokenStorage.hasTokens();
    setIsAuthenticated(hasTokens);
    return hasTokens;
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const response = await adminApi.login(email, password);
      
      if (response.success && response.data.user.role === 'admin') {
        tokenStorage.setTokens({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken
        });
        setIsAuthenticated(true);
      } else {
        throw new Error('Admin yetkisi gereklidir');
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await adminApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    // Check authentication status on app load
    checkAuthStatus();
    setIsLoading(false);
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
